
import ReactMarkdown from 'react-markdown';
import { Message } from '@/lib/session';
import { ProductCard } from '../ProductCard';
import { Skeleton } from '../ui/skeleton';
import { formatMessageTime } from '@/lib/utils';
import { Copy, Check, ThumbsUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface MessageBubbleProps {
  message: Message;
  isLoading?: boolean;
  isNewMessage?: boolean;
}

export const MessageBubble = ({ message, isLoading, isNewMessage = false }: MessageBubbleProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!isLoading && message.content && message.role === 'assistant') {
      if (isNewMessage) {
        // Apply typing animation for new messages
        setIsTyping(true);
        let currentText = '';
        const textToType = message.content;
        let currentIndex = 0;

        const typingInterval = setInterval(() => {
          if (currentIndex < textToType.length) {
            currentText += textToType[currentIndex];
            setDisplayedContent(currentText);
            currentIndex++;
          } else {
            clearInterval(typingInterval);
            setIsTyping(false);
          }
        }, 20); // Adjust speed as needed

        return () => clearInterval(typingInterval);
      } else {
        // For existing messages (from shared links), just show them immediately
        setDisplayedContent(message.content);
        // Optional subtle animation effect can be applied via CSS
      }
    } else if (message.role === 'user') {
      setDisplayedContent(message.content);
    }
  }, [message.content, isLoading, message.role, isNewMessage]);

  const renderComponent = (component: any) => {
    switch (component.type) {
      case 'product-card':
        console.log('MessageBubble rendering product card with props:', component.props);
        return <ProductCard key={component.props.product.id} {...component.props} />;
      default:
        return null;
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const toggleLike = () => {
    setIsLiked(!isLiked);
  };

  return (
    <div
      className={`message group ${
        message.role === 'user' ? 'message-user' : 'message-assistant'
      } ${!isNewMessage && message.role === 'assistant' ? 'animate-fade-in-fast' : ''}`}
    >
      <div className="relative">
        <div className="absolute right-0 top-0 flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={toggleLike}
            className={cn(
              "p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors",
              isLiked && "text-blue-500 hover:text-blue-600 bg-blue-50"
            )}
            aria-label={isLiked ? "Unlike message" : "Like message"}
          >
            <ThumbsUp className="h-4 w-4" />
          </button>
          <button
            onClick={copyToClipboard}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            aria-label="Copy message"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        <div className={cn(
          "prose prose-sm max-w-none pr-20",
          isTyping && "typing-cursor"
        )}>
          {isLoading ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="typing-dot"></div>
                <div className="typing-dot animation-delay-200"></div>
                <div className="typing-dot animation-delay-400"></div>
              </div>
              <Skeleton className="h-4 w-[60%] animate-pulse" />
              <Skeleton className="h-4 w-[80%] animate-pulse" />
              <Skeleton className="h-4 w-[40%] animate-pulse" />
            </div>
          ) : (
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
                p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
                ol: ({ children }) => (
                  <ol className="list-decimal list-outside pl-6 mb-4 space-y-2">
                    {children}
                  </ol>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-outside pl-6 mb-4 space-y-2">
                    {children}
                  </ul>
                ),
                li: ({ children }) => (
                  <li className="pl-2">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-gray-900">{children}</strong>
                ),
                em: ({ children }) => (
                  <em className="italic text-gray-800">{children}</em>
                ),
              }}
            >
              {displayedContent}
            </ReactMarkdown>
          )}
        </div>
        {message.components?.map((component, i) => (
          <div key={i} className="mt-4">
            {renderComponent(component)}
          </div>
        ))}
        {message.timestamp && (
          <div className="mt-2 text-xs text-gray-400">
            {formatMessageTime(message.timestamp)}
          </div>
        )}
      </div>
    </div>
  );
};
